import React from 'react';
import { getSchedulerResource } from '../../shared-server/api/premium-scheduler-api';
import {
  getPremiumBenefits,
  getOffers,
} from '../../shared-server/api/premium-plans/premium-plans';
import { getOfferKey } from '../../shared-server/api/premium-plans/utils';
import HeadContent from '../../next/components/HeadContent';
import BasePage, { type BasePageProps } from '../../next/components/BasePage';
import { getBasePageProps } from '../../next/lib/basePageProps';
import { globalTracer } from 'opentracing';

import { ensureConfigIsSet } from '../../shared-server/api/country-attributes/country-attributes';

import { getUserId, getTrackingId } from '../../next/lib/getUserData';

import type { GetServerSideProps } from 'next';
import type { TextDirection } from '../../shared-server/api/country-attributes/country-attributes';
import type { ServerResponse } from 'http';

import Hero from '../../next/components/molecules/TweakedPremiumHero';
import BenefitsComparison from '../../next/components/molecules/BenefitsComparison';
import Storefront from '../../next/components/molecules/TweakedStorefront';
import Faq from '../../next/components/molecules/TweakedFAQ';

import { getProps as getHeroProps } from '../../next/components/props/hero';

import { getProps as getBenefitsProps } from '../../next/components/props/benefitsComparison';

import { getProps as getStorefrontProps } from '../../next/components/props/storefront';

import { getProps as getFaqProps } from '../../next/components/props/faq';

import getInternalOverride from '../../next/helpers/getInternalOverrides';
import { getRemoteConfig } from '../../shared-server/lib/remote-config/get';

import {
  getHeroOffer,
  getStorefrontPlanOffers,
} from '../../next/lib/process-response';
// import { getMarketingPage } from '../../shared-server/api/premium-marketing/premium-marketing';

import { InternalsRef } from '../../shared-server/lib/remote-config/properties';

export type PageProps = {
  isEvergreen: boolean;
  eligibility: string;
  basePageProps: BasePageProps;
  metadata: {
    fields: {
      title: string;
      description: string;
      image: string;
    };
    market: string;
    direction: TextDirection;
    country: string;
    locale: string;
  };
  components: {
    hero: React.ComponentProps<typeof Hero> | null;
    benefitsComparison: React.ComponentProps<typeof BenefitsComparison> | null;
    storefront?: React.ComponentProps<typeof Storefront> | null; // storefront is not present everywhere
    faq: React.ComponentProps<typeof Faq> | null;
  };
  eventParams?: {
    campaignId?: string;
    offerTypeId?: string;
  };
};

function ensureSuccessApiResponseStatus<T>(
  request: PromiseSettledResult<T>,
  res: ServerResponse,
) {
  if (request.status === 'rejected') {
    res.statusCode = 500;
    throw request.reason;
  }
  return request.value;
}

export const getServerSideProps: GetServerSideProps<
  PageProps,
  { market: string }
> = async props => {
  const { req, res, params, query } = props;

  const span = globalTracer().startSpan('render-nextjs-premium', {
    childOf: req.span,
  });

  const market = params?.market!;
  await ensureConfigIsSet();

  const remoteConfiguration = getRemoteConfig({ req, parentSpan: span });
  const internalOverride = remoteConfiguration.getValue(InternalsRef);

  const userId = getUserId(req) ?? undefined;
  const trackingId = getTrackingId(req);
  const {
    country,
    locale,
    overrideDateTime,
    overrideEligibility,
    overrideWithEnglish,
    isTouchdownInternals,
  } = await getInternalOverride(query, params, internalOverride);

  span.addTags({
    isTouchdownInternals,
    country,
    locale,
    overrideDateTime,
    overrideEligibility,
    overrideWithEnglish,
  });

  // -------------------------------------------------------------------------------
  // API calls

  const [basePageData, middlewareData, premiumBenefitsData] =
    await Promise.allSettled([
      getBasePageProps({
        spotifyMarket: market,
        parentSpan: span,
        req,
      }),
      getSchedulerResource({
        country,
        locale,
        pageName: 'premium-consideration',
        userId,
        trackingId,
        parentSpan: span,
        overrideDateTime,
        overrideEligibility,
        overrideWithEnglish,
      }),
      getPremiumBenefits({
        country,
        locale: [locale],
        parentSpan: span,
      }),
    ]);

  /*  
  WIP - API call to premium-marketing

  const [marketingPage] = await Promise.allSettled([
    getMarketingPage({
      countryCode: country,
      locale: [locale],
      userId,
      parentSpan: span
    })
  ]);
  

  // eslint-disable-next-line no-console
  console.log(marketingPage);

  */

  const basePageResponse = ensureSuccessApiResponseStatus(basePageData, res);
  const middlewareResponse = ensureSuccessApiResponseStatus(
    middlewareData,
    res,
  );
  const benefitsResponse = ensureSuccessApiResponseStatus(
    premiumBenefitsData,
    res,
  );

  // delete scrollingElementAccessor from masthead header navigation
  if (basePageResponse.masthead?.header?.navigation) {
    const headerNavigation = basePageResponse.masthead?.header?.navigation;
    // @ts-ignore
    delete headerNavigation.scrollingElementAccessor;
  }

  const eligibility = middlewareResponse.metadata?.eligibility || 'unknown';

  const isEvergreen = middlewareResponse.metadata?.isEvergreen || false;

  const isIntro = middlewareResponse.metadata?.eligibility === 'INTRO_ELIGIBLE';

  span.addTags({
    eligibility,
    isEvergreen,
    isIntro,
  });

  const heroFromMiddleware = middlewareResponse.content.components?.hero;
  const storefrontFromMiddleware =
    middlewareResponse.content.components.storefront;

  // get offers associated with the hero
  const heroOffer = getHeroOffer(heroFromMiddleware, { locale, country });
  const storefrontPlansOffers = getStorefrontPlanOffers(
    storefrontFromMiddleware.plans,
    { locale, country },
  );

  // construct a payload with hero and storefront offers
  const offersRequest: Parameters<typeof getOffers>[0] = ([] as any)
    .concat([heroOffer])
    .concat(storefrontPlansOffers);
  span.log({
    offersRequest: JSON.stringify(offersRequest),
  });

  /**
   * obtains the previously defined offers from
   * premium plans.
   */
  const offers = await getOffers(offersRequest, { parentSpan: span });

  /**
   * get the hero plan data associated with the fetched
   * offers.
   */

  if (!heroOffer) {
    throw new Error('no error offer specified');
  }

  const heroOfferKey = getOfferKey(heroOffer);
  const heroPlanData = heroOfferKey ? offers[heroOfferKey] : null;

  if (!heroPlanData || !heroPlanData.marketingPlan) {
    throw new Error(`missing hero plan data, key was: ${heroOfferKey}`);
  }

  /**
   * constructs a payload to drive the components
   */

  const heroData = getHeroProps({
    isEvergreen,
    eligibility,
    override: heroFromMiddleware,
    offer: heroPlanData,
    parentSpan: span,
  });
  const benefitsData = getBenefitsProps({
    premiumBenefits: benefitsResponse,
    parentSpan: span,
  });
  const storefrontData = getStorefrontProps({
    showAsWebView: basePageResponse.showAsWebView,
    // eligibility,
    // isEvergreen,
    // isIntro,
    headingBenefits: benefitsResponse, // generic benefits list rendered at the header
    paymentIcons: storefrontFromMiddleware.paymentIcons,
    plans: storefrontFromMiddleware.plans,
    offers,
    country,
    locale,
    parentSpan: span,
    genericBenefitIds:
      benefitsResponse.benefit?.map(benefit => benefit.benefitId!) || [], // TODO: remove?
  });

  const faqData = getFaqProps({
    showAsWebView: basePageResponse.showAsWebView,
    market,
    parentSpan: span,
    eligibility,
  });

  const metadataFields = {
    title: isEvergreen
      ? 'Spotify Premium'
      : `Spotify Premium - ${heroPlanData.checkoutText}`,
    description: `Spotify Premium is a digital music service that gives you access to ad-free music listening of millions of songs. ${
      isEvergreen ? '' : ` ${heroPlanData.checkoutText}.`
    }`,
    image: 'http://www.scdn.co/i/_global/open-graph-default.png',
  };

  span.finish();

  return {
    props: {
      isEvergreen,
      eligibility,
      basePageProps: basePageResponse,
      metadata: {
        fields: metadataFields,
        direction: basePageResponse.direction,
        market: basePageResponse.market,
        country: basePageResponse.country,
        locale,
      },
      components: {
        hero: heroData,
        benefitsComparison: benefitsData,
        storefront: storefrontData,
        faq: faqData,
      },
    },
  };
};

const PremiumPage = ({
  basePageProps,
  metadata,
  components,
  isEvergreen,
  eligibility,
  eventParams,
}: PageProps) => {
  const pageName = 'premium';

  return (
    <>
      <HeadContent {...metadata} />
      <BasePage
        basePageProps={basePageProps}
        isEvergreen={isEvergreen}
        eligibility={eligibility}
        pageName={pageName}
        eventParams={eventParams}
      >
        {!basePageProps.showAsWebView && components.hero && (
          <Hero {...components.hero} />
        )}
        {components.benefitsComparison && (
          <BenefitsComparison {...components.benefitsComparison} />
        )}
        {components.storefront && <Storefront {...components.storefront} />}
        {components.faq && <Faq {...components.faq} />}
      </BasePage>
    </>
  );
};

export default PremiumPage;
